/**
 * Keeps secondary media from competing with the hero image for bandwidth
 * while the page loads.
 *
 * - Slideshow: theme.js preloads the previous and next slides as soon as it
 *   runs, which on mobile put ~350KB of banner images on the wire alongside
 *   the first slide (the LCP element). sections/slideshow.liquid renders every
 *   slide after the first with data-deferred-src / data-deferred-srcset
 *   instead, and they are restored once the window has loaded - or earlier, as
 *   soon as a slide is navigated to.
 * - Category showcase: on collection pages the carousel sits under the
 *   collection banner (the LCP element), and its lazy card images still start
 *   on mobile just before the banner paints - ~300KB competing with it.
 *   sections/category-showcase.liquid renders them the same deferred way,
 *   with a blank GIF as src, when the section is not first on the page.
 * - Video: a <video poster> is fetched eagerly regardless of position, so
 *   sections/video.liquid renders it as data-deferred-poster when the section
 *   is not near the top of the page, and it is restored when the video comes
 *   within 800px of the viewport (the same margin theme.js uses to preload
 *   the video itself).
 *
 * Nothing is deferred in the theme editor, where sections re-render in place.
 */
(() => {
  if (window.deferredMediaInitialized) {
    return;
  }
  window.deferredMediaInitialized = true;

  // <source> elements come before their <img> in document order, so the
  // picture has its candidates in place by the time the img gets its src.
  function restoreSources(root) {
    root.querySelectorAll('[data-deferred-srcset], [data-deferred-src]').forEach((element) => {
      if (element.dataset.deferredSrcset) {
        element.srcset = element.dataset.deferredSrcset;
        delete element.dataset.deferredSrcset;
      }
      if (element.dataset.deferredSrc) {
        element.src = element.dataset.deferredSrc;
        delete element.dataset.deferredSrc;
      }
    });
  }

  // carousel:change does not bubble, but it still passes through document
  // during the capture phase.
  document.addEventListener('carousel:change', (event) => {
    if (event.detail?.cell) {
      restoreSources(event.detail.cell);
    }
  }, true);

  if (document.readyState === 'complete') {
    restoreSources(document);
  } else {
    window.addEventListener('load', () => restoreSources(document), { once: true });
  }

  const posterObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.poster = entry.target.dataset.deferredPoster;
        delete entry.target.dataset.deferredPoster;
        posterObserver.unobserve(entry.target);
      }
    });
  }, { rootMargin: '800px' });

  document.querySelectorAll('video[data-deferred-poster]').forEach((video) => posterObserver.observe(video));
})();
